import type { WcConfig, WcControl, WcFeedback, WcWorkerRecoveryMessage } from '../protocol/WebCodecsTypes';

/** Plain data only: this crosses the worker boundary through structured clone. */
export type WcWorkerStats = {
  epoch: number; receivedBytes: number; framesDecoded: number; framesPresented: number;
  framesDropped: number; reassemblyBytes: number; decoderInFlight: number;
  renderPending: number; oldestPendingAgeUs: number; decodeP95Us: number | null;
  renderIntervalP95Us: number | null; clockUncertaintyUs: number | null;
  maxPresentGapMs: number; lastPresentedAtMs: number; needsKeyframe: boolean;
  /** How long a stalled delta is held for a resend (the measured repair round trip and a margin), and how many keys reassembly stalls have asked for. */
  repairHoldUs: number; keyAsks: number;
};
export type WcClientStats = WcWorkerStats & {
  /** Samples collected since the previous drain, capped at 2048. Main-thread only; never posted. */
  drainLatencySamplesUs(): number[];
};
export type WcClientEvent =
  | { type: 'configured'; config: WcConfig }
  /** `sentUs` is the host sender's clock for the frame; `paintedAtUs` is the client clock when it was drawn. */
  | { type: 'presented'; epoch: number; frameId: number; first: boolean; sentUs?: number; paintedAtUs?: number }
  | { type: 'stalled'; epoch: number; sinceMs: number }
  | { type: 'needsKeyframe'; epoch: number; reason: 'gap' | 'decode-error' | 'epoch' }
  | { type: 'unsupported'; codec: string; message: string }
  | { type: 'failed'; reason: string };
export type WcWorkerInput =
  | { type: 'start'; session: string; canvas: OffscreenCanvas }
  | { type: 'control'; message: WcControl }
  | { type: 'fragment'; bytes: ArrayBuffer; receivedUs: number }
  | { type: 'visibility'; visible: boolean }
  | { type: 'restart' }
  | { type: 'close' };
export type WcWorkerOutput =
  | { type: 'control'; message: WcControl }
  | { type: 'feedback'; message: WcFeedback }
  | { type: 'event'; event: WcClientEvent }
  | { type: 'stats'; stats: WcWorkerStats }
  | { type: 'ingressReleased'; bytes: number }
  | WcWorkerRecoveryMessage;
export interface WcWorkerPort {
  /**
   * Optional in-process startup used when a browser exposes WebCodecs on the page but not a
   * usable decode/render worker. The port owns the canvas without transferring it.
   */
  startWithCanvas?(session: string, canvas: HTMLCanvasElement): void;
  postMessage(message: WcWorkerInput, transfer?: Transferable[]): void;
  onMessage(listener: (message: WcWorkerOutput) => void): () => void;
  terminate(): void;
}
export interface WebCodecsClientOptions {
  createPeer(): RTCPeerConnection;
  createWorker(): WcWorkerPort;
  onCandidate(candidate: RTCIceCandidateInit): void;
  onEvent(event: WcClientEvent): void;
  /** Session-relative microseconds; the injected worker clock MUST share this client-side origin. */
  nowUs(): number;
}
export type WcCompleteFrame = {
  epoch: number; frameId: number; key: boolean; captureUs: number; sentUs: number; durationUs: number;
  receivedUs: number; bytes: Uint8Array;
};
export interface WcVideoFrame {
  readonly timestamp: number;
  readonly codedWidth: number;
  readonly codedHeight: number;
  readonly displayWidth?: number;
  readonly displayHeight?: number;
  close(): void;
}
export interface WcDecoderBackend {
  readonly decodeQueueSize: number;
  configure(config: VideoDecoderConfig): void;
  decode(chunk: EncodedVideoChunkInit): void;
  close(): void;
}
export interface WcDecoderPlatform {
  isConfigSupported(config: VideoDecoderConfig): Promise<VideoDecoderSupport>;
  createDecoder(output: (frame: WcVideoFrame) => void, error: (error: Error) => void): WcDecoderBackend;
}
export interface WcRenderSurface {
  resize(width: number, height: number): void;
  draw(frame: WcVideoFrame): void;
  onLost(listener: () => void): () => void;
}
export interface WcWorkerPlatform extends WcDecoderPlatform {
  nowUs(): number;
  setTimer(callback: () => void, milliseconds: number): number;
  clearTimer(id: number): void;
  requestFrame(callback: () => void): number;
  cancelFrame(id: number): void;
  createSurface(canvas: OffscreenCanvas): WcRenderSurface;
}
export class WcStats {
  /** Cloneable zero snapshot for the worker side. */
  static emptyWorker(): WcWorkerStats {
    return { epoch: 0, receivedBytes: 0, framesDecoded: 0, framesPresented: 0, framesDropped: 0,
      reassemblyBytes: 0, decoderInFlight: 0, renderPending: 0, oldestPendingAgeUs: 0,
      decodeP95Us: null, renderIntervalP95Us: null, clockUncertaintyUs: null,
      maxPresentGapMs: 0, lastPresentedAtMs: 0, needsKeyframe: false, repairHoldUs: 0, keyAsks: 0 };
  }
  static empty(): WcClientStats {
    return { ...WcStats.emptyWorker(), drainLatencySamplesUs: () => [] };
  }
}
